import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ScreenHeader, Surface, Avatar, Icon, Divider } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function nowRecife() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Recife' }));
}

function fmtMoney(v) {
  return `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtHoras(min) {
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m}min`;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ── Barra proporcional ────────────────────────────────────────
function BarPct({ pct, color }) {
  return (
    <div style={{ height: 4, borderRadius: 99, background: 'var(--border-hairline)', overflow: 'hidden', marginTop: 6 }}>
      <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', borderRadius: 99, background: color, transition: 'width .4s ease' }}/>
    </div>
  );
}

// ── Card de stat ──────────────────────────────────────────────
function StatBox({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, padding: '12px 14px', borderRadius: 'var(--radius-md)',
      background: accent ? 'var(--accent-soft)' : 'var(--bg-sunken)',
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
        color: accent ? 'var(--accent)' : 'var(--text-tertiary)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.5, fontFamily: '"Geist Mono", monospace',
        color: accent ? 'var(--accent)' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{sub}</div>}
    </div>
  );
}

export default function Financeiro() {
  const now = nowRecife();
  const [mes, setMes]       = useState(now.getMonth() + 1);
  const [ano, setAno]       = useState(now.getFullYear());
  const [dados, setDados]   = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = useCallback(() => {
    setLoading(true);
    const mesStr = String(mes).padStart(2, '0');
    axios.get(`${API_URL}/financeiro/${mesStr}-${ano}`, { withCredentials: true })
      .then(r => setDados(r.data))
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, [mes, ano]);

  useEffect(() => { buscar(); }, [buscar]);

  const maxValor = dados?.alunos?.length ? dados.alunos[0].valor_total : 1;
  const resumo   = dados?.resumo;

  const selectCss = {
    height: 38, padding: '0 10px', fontSize: 13, fontFamily: 'inherit',
    background: 'var(--bg-sunken)', color: 'var(--text-primary)',
    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
    outline: 'none', cursor: 'pointer',
  };

  return (
    <div className="mema-screen mema-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90, overflow: 'auto' }}>
      <ScreenHeader title="Financeiro"/>

      {/* ── Seletor mês/ano ── */}
      <div style={{ padding: '0 var(--gap-3)', display: 'flex', gap: 8, alignItems: 'center' }}>
        <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ ...selectCss, flex: 2 }}>
          {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))}
          min={2023} max={2100}
          style={{ ...selectCss, flex: 1, textAlign: 'center' }}/>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 'var(--gap-4)', color: 'var(--text-tertiary)', fontSize: 13 }}>Carregando…</div>
      )}

      {!loading && dados && (
        <>
          {/* ── Hero: total do mês ── */}
          <div style={{ padding: '0 var(--gap-3)' }}>
            <Surface style={{
              padding: 'var(--gap-4)',
              background: 'linear-gradient(155deg, var(--accent), color-mix(in oklch, var(--accent) 70%, black))',
              border: 'none', color: '#fff',
              boxShadow: '0 12px 32px color-mix(in oklch, var(--accent) 40%, transparent)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.8, marginBottom: 6 }}>
                {MESES[mes - 1]} {ano} · receita total
              </div>
              <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1.5, lineHeight: 1,
                fontFamily: '"Geist Mono", monospace', fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(resumo.valor_total)}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 12, opacity: 0.85 }}>
                <span>{resumo.aulas} aula{resumo.aulas !== 1 ? 's' : ''}</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{fmtHoras(resumo.minutos)}</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{dados.alunos.length} aluno{dados.alunos.length !== 1 ? 's' : ''}</span>
              </div>
            </Surface>
          </div>

          {/* ── Stats: particular / grupo / fixo ── */}
          {(resumo.particular > 0 || resumo.grupo > 0 || resumo.fixo > 0) && (
            <div style={{ padding: '0 var(--gap-3)', display: 'flex', gap: 'var(--gap-2)' }}>
              {resumo.particular > 0 && (
                <StatBox label="Particular" value={fmtMoney(resumo.particular)} accent/>
              )}
              {resumo.grupo > 0 && (
                <StatBox label="Grupo" value={fmtMoney(resumo.grupo)}/>
              )}
              {resumo.fixo > 0 && (
                <StatBox label="Fixo" value={fmtMoney(resumo.fixo)}/>
              )}
            </div>
          )}

          {/* ── Ranking por aluno ── */}
          <div style={{ padding: '0 var(--gap-3)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
              color: 'var(--text-tertiary)', marginBottom: 'var(--gap-2)' }}>
              Por aluno
            </div>

            {dados.alunos.length === 0 ? (
              <Surface padded style={{ textAlign: 'center' }}>
                <Icon name="calendar" size={26} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
                <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhuma aula aprovada neste mês.</div>
              </Surface>
            ) : (
              <Surface>
                {dados.alunos.map((a, i) => {
                  const pct = maxValor > 0 ? (a.valor_total / maxValor) * 100 : 0;
                  const temParticular = a.particular.aulas > 0;
                  const temGrupo      = a.grupo.aulas > 0;
                  return (
                    <React.Fragment key={a.id}>
                      {i > 0 && <Divider/>}
                      <div style={{ padding: '14px var(--pad-card)' }}>
                        {/* Linha principal */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {/* Posição */}
                          <div style={{
                            width: 22, flexShrink: 0, textAlign: 'center',
                            fontSize: 12, fontWeight: 700,
                            fontFamily: '"Geist Mono", monospace',
                            color: i === 0 ? 'var(--accent)' : 'var(--text-tertiary)',
                          }}>
                            {i + 1}
                          </div>
                          <Avatar name={a.nome} size={36}/>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {a.nome}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              <span>{a.aulas} aula{a.aulas !== 1 ? 's' : ''} · {fmtHoras(a.minutos)}</span>
                              {a.mensal_fixo ? (
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                                  background: 'var(--accent-soft)', color: 'var(--accent)' }}>Fixo</span>
                              ) : (
                                <>
                                  {temParticular && temGrupo && <span>· P+G</span>}
                                  {temParticular && !temGrupo && <span>· Particular</span>}
                                  {!temParticular && temGrupo && <span>· Grupo</span>}
                                </>
                              )}
                            </div>
                            <BarPct pct={pct} color={i === 0 ? 'var(--accent)' : 'var(--border-strong)'}/>
                          </div>
                          <div style={{
                            fontFamily: '"Geist Mono", monospace', fontSize: 15, fontWeight: 700,
                            color: i === 0 ? 'var(--accent)' : 'var(--text-primary)',
                            flexShrink: 0, textAlign: 'right',
                          }}>
                            {fmtMoney(a.valor_total)}
                          </div>
                        </div>

                        {/* Detalhe particular + grupo se tiver os dois */}
                        {temParticular && temGrupo && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10, marginLeft: 70 }}>
                            <div style={{
                              flex: 1, padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                              background: 'color-mix(in oklch, var(--accent) 8%, transparent)',
                              border: '1px solid color-mix(in oklch, var(--accent) 20%, transparent)',
                            }}>
                              <div style={{ fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--accent)', marginBottom: 2 }}>Particular</div>
                              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: '"Geist Mono", monospace', color: 'var(--accent)' }}>{fmtMoney(a.particular.valor)}</div>
                              <div style={{ fontSize: 10, color: 'var(--accent)', opacity: 0.7, marginTop: 1 }}>{a.particular.aulas} aula{a.particular.aulas !== 1 ? 's' : ''} · {fmtHoras(a.particular.minutos)}</div>
                            </div>
                            <div style={{
                              flex: 1, padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                              background: 'color-mix(in oklch, var(--success, #15803d) 8%, transparent)',
                              border: '1px solid color-mix(in oklch, var(--success, #15803d) 20%, transparent)',
                            }}>
                              <div style={{ fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--success, #15803d)', marginBottom: 2 }}>Grupo</div>
                              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: '"Geist Mono", monospace', color: 'var(--success, #15803d)' }}>{fmtMoney(a.grupo.valor)}</div>
                              <div style={{ fontSize: 10, color: 'var(--success, #15803d)', opacity: 0.7, marginTop: 1 }}>{a.grupo.aulas} aula{a.grupo.aulas !== 1 ? 's' : ''} · {fmtHoras(a.grupo.minutos)}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </Surface>
            )}
          </div>

          {/* ── Total final ── */}
          {dados.alunos.length > 0 && (
            <div style={{ padding: '0 var(--gap-3)' }}>
              <Surface padded style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'var(--accent-soft)',
              }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent)' }}>Total de {MESES[mes - 1]}</span>
                <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: 18, fontWeight: 700,
                  color: 'var(--accent)', letterSpacing: -0.5 }}>
                  {fmtMoney(resumo.valor_total)}
                </span>
              </Surface>
            </div>
          )}
        </>
      )}

      {!loading && !dados && (
        <div style={{ padding: '0 var(--gap-3)' }}>
          <Surface padded style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Não foi possível carregar os dados.</div>
          </Surface>
        </div>
      )}
    </div>
  );
}
